import { useEffect, useId, useRef, useState } from 'react'
import { Button, Card, Checkbox, TableScroll, Tag } from '../../ui'
import {
  getAiSettings, getAzureSettings, getWorkspace, inviteMember, startConsent, startMicrosoftLink,
  fetchInvitations, acceptInvitation, declineInvitation, selectWorkspace, INVITABLE_ROLES,
  removeMember, withdrawInvitation, changeMemberRole,
  getNotificationPreferences, setNotificationPreference,
  beginWorkspaceClosure, closeWorkspace,
} from '../../lib/api'
import type {
  WorkspaceSettings, PendingInvitation, WorkspaceMember, InvitableRole, SettingsMutation,
  NotificationPreferences, NotificationChannel, NotificationPreference,
} from '../../lib/api'
import { agrees, personOf, SESSION_CHANGED, SESSION_UNKNOWN, useSession } from './session'
import { describeFailure, useApi, type Load } from './useApi'
import { formatDate } from './LiveFindings'
import { SUPPORT_EMAIL, SupportLink } from '../../lib/support'
import { ExemptAccountsCard } from './ExemptAccountsCard'
import { OrganisationProfileCard } from './OrganisationProfileCard'
import { SettingsTabs, type SettingsTab } from './settingsTabs'
import { AzureSettingsCard } from './AzureSettingsCard'
import { AiSettingsCard } from './AiSettingsCard'
import { MoreAbout, SettingsSection } from './SettingsSection'
import { planName } from './LivePlans'
import { ExchangeRoleSteps } from '../../onboarding/ExchangeRoleSteps'
import { linkedOutcomeMessage } from '../../auth/linkedOutcome'
import './live.css'

/**
 * What this workspace is, and what it can reach.
 *
 * EVERYTHING HERE IS READ. The plan is the plan column, the region is the region column, the
 * members are the members. A settings page that computes a value can disagree with the system it
 * describes, and a customer opens this page precisely when they have started to doubt something —
 * whether the right tenant is connected, who else can see their findings.
 *
 * There is nothing to change here yet, and the page says so rather than offering controls that
 * do not work. Every write this page would need — rename, invite, remove, disconnect — is an
 * endpoint that does not exist, and a disabled button carrying a tooltip is still a promise.
 */
export function LiveSettings({ tab: asked = 'workspace' }: { tab?: SettingsTab }) {
  const session = useSession()
  // Azure: a tab only where Azure assessment is switched on (404 otherwise). An address naming the
  // Azure tab where it is off lands on Workspace, as any unknown tab does.
  const azure = useApi(() => getAzureSettings(), [])
  const withAzure = azure.state === 'error' ? azure.status !== 404 : azure.state === 'ready' || asked === 'azure'
  // AI, likewise: a tab only where bring-your-own AI is switched on. Reloaded after an owner's change.
  const [aiReload, setAiReload] = useState(0)
  const ai = useApi(() => getAiSettings(), [aiReload], undefined, true)
  const withAi = ai.state === 'error' ? ai.status !== 404 : ai.state === 'ready' || asked === 'ai'
  const tab: SettingsTab =
    (asked === 'azure' && azure.state === 'error' && azure.status === 404)
    || (asked === 'ai' && ai.state === 'error' && ai.status === 404)
      ? 'workspace'
      : asked
  // A reload key bumped after a member change re-fetches the list; keepPrevious (the 4th arg) leaves
  // the table on screen while the refresh is in flight rather than flashing to a spinner.
  const [reload, setReload] = useState(0)
  const settings = useApi(() => getWorkspace(), [reload], undefined, true)
  // The signed-in member's own notification preferences, on the SAME reload generation so one bump
  // refreshes both reads. keepPrevious leaves the card on screen during a refresh.
  const notifications = useApi(() => getNotificationPreferences(), [reload], undefined, true)

  // ONE lock and ONE refresh path for every settings write on the page — invite, remove, withdraw,
  // role, and a notification toggle — so two writes cannot overlap and each write's controls stay
  // disabled until the refresh that write asked for has landed. It watches BOTH reads and releases
  // only once both have settled, so a notification write's refresh cannot be "finished" by the
  // unrelated workspace GET settling first. Created unconditionally (before the early returns) so the
  // hook order is stable.
  const mutations = useSettingsMutations([settings, notifications], () => setReload((n) => n + 1))

  /*
   * ACCOUNT-LEVEL CARDS RENDER BEFORE THE WORKSPACE GATE, and that ordering is the fix.
   *
   * getWorkspace() needs workspace scope and fails for somebody who has none -- which is exactly
   * the person who most needs to see an invitation. The component returned its error first, so an
   * invited newcomer could never reach the screen that would let them accept, and was sent to
   * "connect a directory" instead. Their invitation was unreachable in the product that sent it.
   *
   * Connecting Microsoft belongs here for the same reason: it is a property of the ACCOUNT, not of
   * a workspace, and it was previously buried inside the tenant card where it needed a directory
   * to already be connected.
   */
  // A pending invitation is something to act on, so it stays above the tabs on every tab (it renders
  // nothing when there is none); your sign-in lives on Your account.
  //
  // THE SAME SHAPE IN EVERY STATE: invitations, the tab bar, then the panel. Loading, error and
  // ready put the account card at the same place in the tree, so a person who focused "Connect
  // Microsoft sign-in" while the page loaded keeps that focus when the workspace answers -- or fails
  // to -- instead of the card being rebuilt under them.
  const invitations = <div className="settings-invitations"><Invitations /></div>

  // Loading: this tab's own card, not every tab's. The whole account block used to render here on
  // every tab, flashing "Your sign-in" on /settings/team and pulling it away when the workspace came.
  if (settings.state === 'loading') {
    return (
      <>
        {invitations}
        <div className="settings-layout">
          <SettingsTabs current={tab} withAzure={withAzure} withAi={withAi} />
          <div className="settings-main">
            {tab === 'account' ? <div className="settings-panel"><AccountSignIn /></div> : null}
            <div className="live-empty">Loading…</div>
          </div>
        </div>
      </>
    )
  }

  if (settings.state === 'error') {
    // Your sign-in on EVERY tab here: a person with no workspace yet (an invited newcomer, somebody
    // who must connect Microsoft sign-in before connecting a tenant) has nothing else on this page.
    //
    // A focusable alert, not a bare <div>: this branch also REPLACES the member table when a refresh
    // triggered by a mutation fails, so a keyboard user who just confirmed a removal would otherwise
    // be left with focus on the vanished status region (i.e. on document.body) and no announcement.
    // Focus is taken only when a mutation caused it (busy still true); an initial-load error announces
    // without stealing focus from the account card above.
    return (
      <>
        {invitations}
        <div className="settings-layout">
          <SettingsTabs current={tab} withAzure={withAzure} withAi={withAi} />
          <div className="settings-main">
            <div className="settings-panel"><AccountSignIn /></div>
            <TerminalNotice focusOnMount={mutations.busy}>{describeFailure(settings.status)}</TerminalNotice>
          </div>
        </div>
      </>
    )
  }

  if (!agrees(session, settings.data.workspaceId)) {
    // Likewise a card-replacing outcome: a refresh can return a workspace this session no longer
    // names (an account switch in another tab). Announce it, and take focus only when a mutation
    // caused the replacement.
    return (
      <TerminalNotice focusOnMount={mutations.busy}>
        {session.state === 'unknown' ? SESSION_UNKNOWN : SESSION_CHANGED}
      </TerminalNotice>
    )
  }

  const w = settings.data

  // Changing the emergency and service account list is an owner's; the card hides its controls from
  // everybody else (the server decides).
  const myEmail = personOf(session)?.email?.toLowerCase() ?? null
  const isOwner =
    myEmail !== null &&
    w.members.some((m) => m.email.toLowerCase() === myEmail && m.role === 'owner' && m.status === 'active')

  return (
    <>
      {invitations}

      {/* The grouped menu beside the page it opens (owner approved, 2026-10-11): Workspace,
          Connections and Personal say whose setting each page holds. */}
      <div className="settings-layout">
      <SettingsTabs current={tab} withAzure={withAzure} withAi={withAi} />

      <div className="settings-main">
      <div className="settings-panel">
        {tab === 'workspace' ? (
          <>
            <SettingsSection title="Workspace">
              <dl className="settings-grid">
                <dt>Name</dt>
                <dd>{w.name}</dd>

                <dt>Plan</dt>
                <dd>
                  {planName(w.plan)} <a href="/plans" className="settings-inline-link">See plans</a>
                </dd>

                <dt>Data region</dt>
                <dd>
                  {regionName(w.dataRegion)} <span className="mono text-muted">({w.dataRegion})</span>
                  <p className="settings-note">
                    Where this workspace's findings and evidence are stored. It is fixed when the
                    workspace is created and cannot be changed afterwards.
                  </p>
                </dd>

                <dt>Workspace ID</dt>
                <dd>
                  <span className="mono">{w.workspaceId}</span> <CopyValue value={w.workspaceId} label="workspace ID" />
                </dd>
              </dl>
            </SettingsSection>

            {/* What kind of organisation this is (0175): decides whether a control CISA sets only
                for US federal civilian agencies is assessed. Everyone reads it; an owner or admin
                changes it, through the page's one write lock. */}
            <OrganisationProfileCard w={w} mutations={mutations} />

            {/* Owners only; renders nothing for anybody else. Last on the tab, where a destructive
                control belongs. */}
            <CloseWorkspaceCard w={w} mutations={mutations} />
          </>
        ) : null}

        {tab === 'tenant' ? (
          <SettingsSection title="Microsoft tenant">
            {w.connection === null ? (
              // NOT an empty table. "No tenant is connected" and "a tenant is connected and has
              // nothing to say" look identical as a blank row, and only one of them means no
              // assessment can run.
              <p className="live-empty">
                No Microsoft tenant is connected, so nothing can be assessed.{' '}
                {/* It said "Connect one from Onboarding" -- a page production does not have. */}
                <a href="/connect">Connect Microsoft 365</a>
              </p>
            ) : (
              <dl className="settings-grid">
                <dt>Directory ID</dt>
                <dd>
                  <span className="mono">{w.connection.tenantId}</span>
                  <p className="settings-note">
                    Check this against your own tenant in the Microsoft admin centre. It is the one
                    value that confirms which directory we read without having to take our word for
                    it.
                  </p>
                </dd>

                <dt>Status</dt>
                <dd>
                  <ConnectionStatus status={w.connection.status} />
                  {w.connection.verifiedAt ? (
                    <p className="settings-note">
                      Last verified {formatDate(w.connection.verifiedAt)}.
                    </p>
                  ) : null}
                </dd>

                {/*
                  WHAT DEFENSSIVE DOES, NOT WHAT IT IS CAPABLE OF. This said "Read-only. Defenssive
                  holds read permissions in your tenant and cannot change a setting". The Exchange
                  permission on the consent screen is not read-only by name (the privacy notice says so
                  in as many words), and the Entra role reference lists "Read and configure Service
                  Health" in both Global Reader and Security Reader -- so "cannot" was a capability
                  claim that is not ours to make (docs/what-role-we-ask-a-customer-for.md, "Can you
                  change anything?"). What Defenssive does is only read, which is the promise the
                  Connect screen and the privacy notice make, and the notice says what keeps the
                  Exchange permissions' use to reads. TWO Exchange permissions, as the corrected
                  Terms and privacy notice say (Exchange.ManageAsApp, and Exchange.ManageAsAppV2,
                  which the product does not call): it said "Its Exchange permission", one.
                */}
                <dt>Access</dt>
                <dd>
                  Reads only.
                  <MoreAbout label="More about access">
                    <p className="settings-note">
                      Defenssive only reads: it makes no change in your tenant, including to the
                      settings it reports on. It holds two Exchange permissions, and neither is
                      read-only by name: what they allow is set by the roles you assign to the
                      Defenssive application. The{' '}
                      <a href="/privacy" target="_blank" rel="noopener">
                        privacy notice
                      </a>{' '}
                      explains what keeps its use of them to reads. Remediation steps are for you to
                      carry out.
                    </p>
                  </MoreAbout>
                </dd>

                <dt>Permissions</dt>
                <dd>
                  <Reapprove />
                </dd>

                {/*
                  THE STEP RE-APPROVING CANNOT DO, where a customer comes back to look for it. The
                  Connect screen names it before consent, but the role can only be assigned after
                  consent has created our application in the tenant -- so it has to be findable here
                  too. Stated as a requirement, not a status: nothing on this page knows whether the
                  role is assigned, and saying either would be a guess. It opened with "Assigned by an
                  administrator in Microsoft Entra.", which in a grid of states (Status, Access) reads
                  as "the role is assigned" -- so the value now says what the role is needed for, and
                  the note says outright that this page cannot tell. Not "Needs to be assigned" either:
                  that reads as "is not assigned", the opposite guess.
                */}
                <dt>Exchange Online role</dt>
                <dd>
                  Needed for Exchange Online and Defender for Office 365 checks.
                  <p className="settings-note">
                    An administrator assigns this directory role to the Defenssive application in
                    Microsoft Entra, once. Approving permissions cannot assign one. This page cannot
                    tell whether it has been done; Microsoft Entra shows it, on the role&rsquo;s page
                    under Roles &amp; admins.
                  </p>
                  {/* The steps and Microsoft's refusals under each role, folded: the Connect screen
                      showed them before consent, and here they are what an administrator opens
                      once, when assigning the role. */}
                  <MoreAbout label="How to assign the role">
                    <ExchangeRoleSteps />
                  </MoreAbout>
                </dd>
              </dl>
            )}
          </SettingsSection>
        ) : null}

        {tab === 'azure' ? <AzureSettingsCard azure={azure} /> : null}

        {tab === 'ai' ? <AiSettingsCard ai={ai} isOwner={isOwner} onChanged={() => setAiReload((n) => n + 1)} /> : null}

        {tab === 'team' ? (
          <>
            <MembersCard w={w} mutations={mutations} />
            <InvitePanel workspace={w} mutations={mutations} />
          </>
        ) : null}

        {/* The accounts the checks may accept as exceptions (0154). Owner-changed, everyone-read. */}
        {tab === 'emergency-accounts' ? <ExemptAccountsCard isOwner={isOwner} /> : null}

        {tab === 'account' ? (
          <>
            <AccountSignIn />
            <NotificationsCard
              workspaceId={w.workspaceId}
              notifications={notifications}
              mutations={mutations}
            />
          </>
        ) : null}
      </div>
      </div>
      </div>
    </>
  )
}

/**
 * A page-replacing error/scope message that a screen reader always announces (role="alert"), and that
 * takes focus ONLY when a member write's refresh caused the replacement.
 *
 * The distinction matters. When a mutation's refresh fails mid-interaction, the card the person was
 * working in vanishes and their focus would fall to document.body, so moving it here is a rescue. But
 * on an INITIAL load failure the account cards above are already usable — someone may have focused
 * "Connect Microsoft sign-in" or an invitation — and grabbing focus into a workspace error they were
 * not interacting with would be the theft, not the rescue. `focusOnMount` (the page-wide `busy`, still
 * true at the render that first shows this branch after a refresh) tells the two apart; the alert role
 * announces either way.
 */
function TerminalNotice({ focusOnMount, children }: { focusOnMount: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (focusOnMount) ref.current?.focus()
    // Read once, at mount: whether THIS appearance interrupted a mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div ref={ref} tabIndex={-1} role="alert" className="live-error" style={{ outline: 'none' }}>
      {children}
    </div>
  )
}

/**
 * The one lock and the one refresh path shared by every member write on this page.
 *
 * Sharing is the point. Invite, remove, withdraw and role-change all mutate the same member list and
 * all reconcile through the same GET, so they must not overlap and must not each keep their own idea
 * of "busy". A single controller gives the whole page one `busy` and one `run`, and holds the lock
 * from the start of a write until the refresh THAT WRITE requested has landed — not merely until the
 * next render, and not per-card.
 */
export interface SettingsMutations {
  /** True from the first line of a write until the refresh it triggered settles. */
  busy: boolean
  /**
   * Run one member-list write under the shared lock. `onStart` fires SYNCHRONOUSLY before the
   * request — move focus here, so it is never stranded on a control (a Confirm button) that the
   * write is about to unmount, even when the request is slow. `onResult` fires with the server's
   * answer. The controller then refreshes on success or on a stale-view refusal, and releases the
   * lock only once that refresh has landed.
   */
  run: (
    action: () => Promise<SettingsMutation>,
    handlers: { onStart?: () => void; onResult?: (result: SettingsMutation) => void },
  ) => Promise<void>
}

function useSettingsMutations(
  loads: ReadonlyArray<Load<unknown>>,
  refresh: () => void,
): SettingsMutations {
  const [busy, setBusy] = useState(false)
  // A ref beside the state: the state drives the UI, the ref makes the guard synchronous so two
  // clicks landing in the same tick cannot both pass it.
  const busyRef = useRef(false)
  const awaitingRefresh = useRef(false)
  // Anyone awaiting `run()` for the WHOLE operation (write + its refresh) parks a resolver here; it
  // fires the moment the lock is released, so a caller like the invite form can keep its own
  // "in progress" state (the "Inviting…" label) true until the refresh has actually landed.
  const releaseWaiters = useRef<Array<() => void>>([])

  const release = () => {
    busyRef.current = false
    setBusy(false)
    const waiters = releaseWaiters.current
    releaseWaiters.current = []
    for (const resolve of waiters) resolve()
  }

  // Release only when the refresh THIS controller asked for has landed on EVERY watched read. Writes
  // are serialised by the lock, so at most one refresh is ever outstanding. We compare the LOAD OBJECTS
  // themselves: useApi replaces the object on every completed fetch (a new one even for a repeat error),
  // and keepPrevious means it is NOT replaced merely by a refetch starting — so "the object differs from
  // the one captured when the refresh was requested" is exactly "this read has re-settled", detectable
  // even when a read errors and re-errors identically. Releasing only when ALL have advanced is what
  // stops a notification write being declared finished by the unrelated workspace GET settling first.
  const awaitingFrom = useRef<ReadonlyArray<Load<unknown>> | null>(null)
  useEffect(() => {
    if (!awaitingRefresh.current || awaitingFrom.current === null) return
    const from = awaitingFrom.current
    if (loads.length === from.length && loads.every((l, i) => l !== from[i])) {
      awaitingRefresh.current = false
      awaitingFrom.current = null
      release()
    }
    // Keyed on the watched load objects (stable length across renders); `release` sets refs/state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, loads)

  const run: SettingsMutations['run'] = async (action, handlers) => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    handlers.onStart?.()

    const result = await action()
    handlers.onResult?.(result)

    // Refresh on success, and on any refusal that means the VIEW is stale: a row changed under us
    // (404/409), or my own authorization changed (401/403 — an ownership hand-off, or an account
    // switch in another tab). The refresh reconciles the list and recomputes who may manage it. A bad
    // input (400) or a dropped connection (0) establishes nothing, so the view stays and the lock
    // releases at once.
    if (result.ok || [401, 403, 404, 409].includes(result.status)) {
      // Do NOT resolve run() yet — park a waiter and let the release effect resolve it once the
      // refresh settles on EVERY watched read, so the operation is only "finished" when the page
      // actually reflects it. Snapshot the pre-refresh identities so the effect can tell which reads
      // have since advanced.
      const refreshed = new Promise<void>((resolve) => releaseWaiters.current.push(resolve))
      awaitingFrom.current = loads
      awaitingRefresh.current = true
      refresh()
      await refreshed
    } else {
      release()
    }
  }

  return { busy, run }
}

const INVITABLE_VALUES: readonly string[] = INVITABLE_ROLES.map((r) => r.value)

function isInvitable(role: string): role is InvitableRole {
  return INVITABLE_VALUES.includes(role)
}

function roleLabel(value: string): string {
  return INVITABLE_ROLES.find((r) => r.value === value)?.label ?? value
}

/** An Azure region code as a person reads it. The code stays beside it (it is what a DPA names). */
function regionName(code: string): string {
  const names: Record<string, string> = {
    eastus: 'East US',
    eastus2: 'East US 2',
    westeurope: 'West Europe',
    northeurope: 'North Europe',
    uksouth: 'UK South',
  }
  return names[code] ?? code
}

/** A member's role in words -- the same words the invite form uses, plus Owner, which is never
 *  invitable. The table printed the stored value ("read_only"), a database word, not a person's. */
function memberRoleLabel(value: string): string {
  return value === 'owner' ? 'Owner' : roleLabel(value)
}

/** Sort order for the members table: who can read now, who might soon, who no longer can. */
function memberStatusRank(status: string): number {
  return status === 'active' ? 0 : status === 'pending' ? 1 : 2
}

/** A membership's state in words. "pending" said nothing about what was pending; "suspended" is how
 *  the database records a removal. An unknown future value is shown as it is, never guessed at. */
function memberStatusLabel(value: string): string {
  switch (value) {
    case 'active':
      return 'Active'
    case 'pending':
      return 'Invited — not joined yet'
    case 'suspended':
      return 'Removed'
    default:
      return value
  }
}

/** Why notification mail to an address is paused, in plain words (provider feedback, migration 0148). */
function pausedReason(reason: string | null | undefined): string {
  switch (reason) {
    case 'hard_bounce':
      return 'your mailbox rejected one of them.'
    case 'spam_complaint':
      return 'one of them was reported as spam from your mailbox.'
    case 'manual_suppression':
      return 'our email provider has stopped delivery to your address.'
    default:
      return 'our email provider reported a delivery problem.'
  }
}

/**
 * A member's OWN notification preferences for this workspace: the two channels the product actually
 * sends, and honest wording about what each is.
 *
 * PER-USER, not owner-gated: every active member controls whether THEY are emailed (the server derives
 * the actor from the session and can only ever change that person's row). A checkbox reflects EFFECTIVE
 * audience membership — owners/admins are in by default, others must opt in — and toggling writes an
 * explicit enabled/disabled. Two honesty rules the copy holds: these emails are part of Monitor and are
 * not sent while monitoring is inactive (the toggle is still saved), and neither channel is a real-time
 * feed — the summary is weekly and may be empty, the change alert follows a scheduled/onboarding scan
 * (not live Microsoft changes, not manual assessments, not resolved findings).
 *
 * Renders controls only when the preferences response, this workspace, and the session all name the same
 * tenant — a stale cross-tab response never drives a write against the wrong workspace.
 */
export function NotificationsCard({
  workspaceId,
  notifications,
  mutations,
}: {
  workspaceId: string
  notifications: Load<NotificationPreferences>
  mutations: SettingsMutations
}) {
  const session = useSession()
  const busy = mutations.busy
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const statusRef = useRef<HTMLDivElement>(null)

  function set(channel: NotificationChannel, next: boolean, success: string) {
    const preference: NotificationPreference = next ? 'enabled' : 'disabled'
    void mutations.run(() => setNotificationPreference(channel, preference, workspaceId), {
      onStart: () => {
        setError(null)
        setNotice(null)
        statusRef.current?.focus()
      },
      onResult: (result) => {
        if (result.ok) setNotice(success)
        else setError(result.message)
        statusRef.current?.focus()
      },
    })
  }

  const body = () => {
    if (notifications.state === 'loading') {
      return <p className="settings-note">Loading…</p>
    }
    // Fail closed on anything that would let a control act against the wrong tenant: an error, or a
    // response that names a different workspace than the page/session (a switch in another tab).
    if (
      notifications.state !== 'ready' ||
      notifications.data.workspaceId !== workspaceId ||
      !agrees(session, notifications.data.workspaceId)
    ) {
      return (
        <p className="settings-note">
          Your notification preferences could not be loaded just now.
        </p>
      )
    }

    const n = notifications.data

    return (
      <>
        {!n.monitoringActive ? (
          <p className="report-warning">
            These emails are part of Monitor, which isn’t active on this workspace right now — so none
            are being sent. Your choices below are saved for if monitoring becomes active.
          </p>
        ) : null}

        {n.delivery?.paused ? (
          <p className="report-warning" role="status">
            Notification emails to your address are paused: {pausedReason(n.delivery.reason)} Sign-in
            emails are not affected. Once your mailbox accepts our mail again, email{' '}
            <SupportLink subject="Resume my notification emails" /> to resume them.
          </p>
        ) : null}

        <div className="notif-option">
          <Checkbox
            label="Weekly Monitor summary"
            checked={n.digest.eligible}
            disabled={busy}
            onChange={(e) =>
              set(
                'digest',
                e.currentTarget.checked,
                e.currentTarget.checked ? 'Weekly summary turned on.' : 'Weekly summary turned off.',
              )
            }
          />
          <p className="settings-note">
            A weekly email summarising this workspace’s security posture while it’s on Monitor. A quiet,
            fully-assessed week may send nothing. Findings that have been resolved appear here.
          </p>
        </div>

        <div className="notif-option">
          <Checkbox
            label="Change alerts"
            checked={n.alerts.eligible}
            disabled={busy}
            onChange={(e) =>
              set(
                'alerts',
                e.currentTarget.checked,
                e.currentTarget.checked ? 'Change alerts turned on.' : 'Change alerts turned off.',
              )
            }
          />
          <p className="settings-note">
            An email after a scheduled or onboarding assessment records a new or returning finding, or
            when an assessment can’t complete. It follows a scan — not live Microsoft changes — and does
            not cover assessments you run yourself or findings that have been resolved.
          </p>
        </div>

        <div
          ref={statusRef}
          tabIndex={-1}
          aria-live="polite"
          style={{ outline: 'none', marginTop: 'var(--space-2)' }}
        >
          {error ? (
            <p className="live-error" role="alert">
              {error}
            </p>
          ) : busy ? (
            <p className="settings-note">Saving…</p>
          ) : notice ? (
            <p className="settings-note">{notice}</p>
          ) : null}
        </div>
      </>
    )
  }

  return (
    <SettingsSection
      title="Email notifications"
      description={
        <p>
          Owners and admins receive these by default; other members receive them only if they turn
          them on here. This controls what <strong style={{ fontWeight: 600 }}>you</strong> are
          emailed, not anyone else.
        </p>
      }
    >
      {body()}
    </SettingsSection>
  )
}

/**
 * Who can see this workspace, and — for an owner — the controls to change it.
 *
 * The rules are the database's (owner only, from a session that names this workspace, never touching
 * an owner); this shows the controls only to someone the server would let act, and surfaces the
 * server's own refusal verbatim when it does not. Owner-detection here is a PRESENTATION signal, never
 * the security boundary — the server is that.
 */
export function MembersCard({ w, mutations }: { w: WorkspaceSettings; mutations: SettingsMutations }) {
  const session = useSession()
  const myEmail = personOf(session)?.email?.toLowerCase() ?? null

  // An owner sees the controls. The database column is citext but JS equality is case-sensitive, so
  // lowercase both. A missing / loading / unknown identity, or no active-owner row for me, hides the
  // controls — fail closed, never assume permission. Ordering (owner-first) is not a signal.
  const canManage =
    myEmail !== null &&
    w.members.some(
      (m) => m.email.toLowerCase() === myEmail && m.role === 'owner' && m.status === 'active',
    )

  // The lock is the page-wide one (shared with the invite form via `mutations`), so a table write and
  // an invite cannot overlap. Two live regions surface the outcome here: an assertive alert for a
  // refusal, a polite one for success/progress — kept at TABLE level so a refusal about a row the
  // refresh then removes (a withdrawn invitation) is still announced.
  const busy = mutations.busy
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const statusRef = useRef<HTMLDivElement>(null)

  function act(action: () => Promise<SettingsMutation>, success: string) {
    void mutations.run(action, {
      // BEFORE the request, not after it: clear the last outcome and move focus to the status region
      // now, so a keyboard user is never stranded on the Confirm button this write is about to
      // unmount — even if the request is slow.
      onStart: () => {
        setError(null)
        setNotice(null)
        statusRef.current?.focus()
      },
      // The server has answered. Announce success in its own right or surface the refusal verbatim;
      // the controller decides whether to refresh (and holds the lock until that lands). Re-assert
      // focus so the outcome, now rendered, is what a screen reader announces.
      onResult: (result) => {
        if (result.ok) setNotice(success)
        else setError(result.message)
        statusRef.current?.focus()
      },
    })
  }

  return (
    <SettingsSection
      title="Who can see this workspace"
      description={
        <p>
          Everyone listed as <strong>Active</strong> can read every finding in this workspace,
          including the evidence, which carries your users' names and your policy contents. People
          still invited, or removed, cannot.
        </p>
      }
    >

      <TableScroll label="Workspace members">
      <table className="settings-table">
        <thead>
          <tr>
            <th scope="col">Person</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            {canManage ? <th scope="col">Manage</th> : null}
          </tr>
        </thead>
        <tbody>
          {/* Active first, then invitations, then removed: the order in which they matter. */}
          {[...w.members].sort((a, b) => memberStatusRank(a.status) - memberStatusRank(b.status)).map((m) => (
            <MemberRow
              key={m.email}
              m={m}
              canManage={canManage}
              busy={busy}
              onRemove={() => act(() => removeMember(m.email), `${m.email} was removed.`)}
              onWithdraw={() =>
                act(() => withdrawInvitation(m.email), `The invitation to ${m.email} was withdrawn.`)
              }
              onRole={(role) =>
                act(() => changeMemberRole(m.email, role), `${m.email} is now ${roleLabel(role)}.`)
              }
            />
          ))}
        </tbody>
      </table>
      </TableScroll>

      <div ref={statusRef} tabIndex={-1} style={{ outline: 'none' }}>
        {error ? (
          <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
            {error}
          </p>
        ) : null}
        <p className="settings-note" aria-live="polite">{busy ? 'Working…' : notice}</p>
      </div>

      {!canManage ? (
        <p className="settings-note">
          Only an owner can remove members or change roles. Ask an owner of this workspace to make the
          change.
        </p>
      ) : null}
    </SettingsSection>
  )
}

function MemberRow({
  m,
  canManage,
  busy,
  onRemove,
  onWithdraw,
  onRole,
}: {
  m: WorkspaceMember
  canManage: boolean
  busy: boolean
  onRemove: () => void
  onWithdraw: () => void
  onRole: (role: InvitableRole) => void
}) {
  // A proposed role, applied only on an explicit "Save role" click. Posting from onChange is one
  // stray keystroke away from a silent, consequential promotion or demotion.
  const [proposed, setProposed] = useState<InvitableRole | ''>(
    isInvitable(m.role) ? m.role : '',
  )

  // Reconcile a role that changed underneath us. A refresh replaces this row's `m` with the server's
  // truth (another owner re-roled this person, or my own last save landed); follow it ONLY while the
  // draft is still pristine — equal to what the select last showed for this row, or empty. An
  // unsaved selection the viewer made is theirs and is preserved across the refresh.
  const seenRole = useRef<string>(m.role)
  useEffect(() => {
    if (m.role !== seenRole.current) {
      setProposed((current) =>
        current === '' || current === seenRole.current ? (isInvitable(m.role) ? m.role : '') : current,
      )
      seenRole.current = m.role
    }
  }, [m.role])

  const cells = (
    <>
      <td className="settings-member-email">{m.email}</td>
      <td>{memberRoleLabel(m.role)}</td>
      <td>{memberStatusLabel(m.status)}</td>
    </>
  )

  if (!canManage) return <tr>{cells}</tr>

  // Owner (including the viewer) and an already-removed member have no actions: an owner is never a
  // valid target — which also prevents self-removal — and a suspended row is already gone. An
  // unexpected future status stays inert rather than guessing an action for it.
  let manage = <span className="text-muted">—</span>

  if (m.role !== 'owner' && m.status === 'pending') {
    // A pending invitee has no access yet, so the Remove wording ("ends their access") would be a
    // lie; withdrawing cancels an invitation that was never accepted.
    manage = (
      <ConfirmAction
        verb="Withdraw"
        subject={m.email}
        prompt={`Withdraw the invitation to ${m.email}? They have not joined, so this simply cancels it.`}
        onConfirm={onWithdraw}
        disabled={busy}
      />
    )
  } else if (m.role !== 'owner' && m.status === 'active') {
    manage = (
      <span
        style={{ display: 'inline-flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}
      >
        <select
          aria-label={`Role for ${m.email}`}
          value={proposed}
          disabled={busy}
          onChange={(e) => setProposed(e.target.value as InvitableRole)}
        >
          {INVITABLE_ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <Button
          variant="secondary"
          // Per-person accessible name: a table of identical "Save role" buttons is unnavigable by
          // screen reader. The visible text stays "Save role", which the accessible name contains.
          aria-label={`Save role for ${m.email}`}
          disabled={busy || proposed === '' || proposed === m.role}
          onClick={() => {
            if (proposed !== '' && proposed !== m.role) onRole(proposed)
          }}
        >
          Save role
        </Button>
        <ConfirmAction
          verb="Remove"
          subject={m.email}
          prompt={`Remove ${m.email}? This ends their access to this workspace.`}
          onConfirm={onRemove}
          disabled={busy}
        />
      </span>
    )
  }

  return (
    <tr>
      {cells}
      <td>{manage}</td>
    </tr>
  )
}

/** Where a closed workspace's owner lands: sign-in, with the notice that says what just happened. */
function goToSignInAfterClosure() {
  window.location.assign('/auth/sign-in?workspace_closed=1')
}

/**
 * Closing this workspace — the most destructive act in the product, so it is owner-only, takes two
 * deliberate human steps, and says exactly what it is.
 *
 * Step one asks the server to start (auth.begin_workspace_closure mints a single-use confirmation valid
 * for five minutes); step two needs the workspace's name typed back before the button will fire, and
 * redeems that confirmation. The server stays the authority — owner, from a session naming this
 * workspace — and its refusals are shown verbatim; owner-detection here only decides whether to render.
 *
 * HONEST ABOUT WHAT IT IS: closure is ACCESS SHUTDOWN, NOT DELETION (see go-live-readiness §C). The copy
 * never calls it deletion, and says where the data still is and how to ask for more.
 */
export function CloseWorkspaceCard({
  w,
  mutations,
  onClosed = goToSignInAfterClosure,
}: {
  w: WorkspaceSettings
  mutations: SettingsMutations
  /** Called once the workspace is closed (every session, this one included, is gone). */
  onClosed?: () => void
}) {
  const session = useSession()
  const myEmail = personOf(session)?.email?.toLowerCase() ?? null
  const isOwner =
    myEmail !== null &&
    w.members.some(
      (m) => m.email.toLowerCase() === myEmail && m.role === 'owner' && m.status === 'active',
    )

  const [token, setToken] = useState<string | null>(null)
  const [typed, setTyped] = useState('')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const startRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  const returnFocus = useRef(false)
  const promptId = useId()

  useEffect(() => {
    if (token !== null) inputRef.current?.focus()
    else if (returnFocus.current) {
      returnFocus.current = false
      startRef.current?.focus()
    }
  }, [token])

  if (!isOwner) return null

  // What must be typed back. The workspace's own name; a fixed phrase if it somehow has none, so an
  // empty name can never make the check trivially true.
  const phrase = w.name.trim() || 'close workspace'
  const matches = typed.trim() === phrase
  const busy = working || mutations.busy

  async function start() {
    setError(null)
    setWorking(true)
    const begun = await beginWorkspaceClosure()
    setWorking(false)
    if (begun.ok) {
      setTyped('')
      setToken(begun.token)
    } else {
      setError(begun.message)
      statusRef.current?.focus()
    }
  }

  function cancel() {
    // The confirmation simply lapses server-side; nothing to undo.
    returnFocus.current = true
    setTyped('')
    setToken(null)
  }

  async function close() {
    if (token === null || !matches) return
    setError(null)
    setWorking(true)
    const result = await closeWorkspace(token)
    if (result.ok) {
      onClosed()
      return
    }
    // A refused or expired confirmation is single-use: start again from step one.
    setWorking(false)
    setToken(null)
    setTyped('')
    setError(result.message)
    statusRef.current?.focus()
  }

  return (
    <SettingsSection title="Close this workspace" tone="danger">
      <p className="settings-lede">
        Closing is permanent and takes effect for everyone the moment it completes:
      </p>
      <ul className="plan-list">
        <li>Scheduled assessments, change alerts and weekly summaries stop, and the Monitor plan is cancelled.</li>
        {/* Not "Defenssive stops reading your tenant": a read already under way when the closure
            lands is not recalled (0138), which the Terms and the privacy notice both say. */}
        <li>
          The Microsoft 365 connection is disconnected: nothing new is read from your tenant, though
          a read already under way can finish.
        </li>
        <li>Everyone is signed out, and every membership — including yours — is suspended.</li>
        <li>Nothing new can be written for this workspace, and it cannot be reopened.</li>
        {/* 0179: a closed workspace no longer holds its tenant. Before, it held it for good and the
            organisation could never connect it again without an operator's purge. */}
        <li>
          Your Microsoft 365 tenant is released: an administrator of it can connect it to a new
          workspace later.
        </li>
      </ul>
      {/*
        THE PRIVACY NOTICE'S BACKUP FACTS, NOT "our encrypted backups". Azure encrypts the backup
        storage with keys Microsoft manages, and each backup holds the workspace's wrapped data key,
        which our Key Vault key can still unwrap -- so a backup stays readable by us until it is
        gone. The lifecycle rule deletes a backup about 90 days after it is written and soft delete
        keeps it about 30 days more: about four months at most. The earlier pilot environment's
        backup account is not on that schedule, and the notice says so. Worded without "is deleted"
        or "are deleted", which the card's test forbids so that closure is never read as deleting.
      */}
      {/* Folded, not removed: what happens to stored data and backups is what a careful owner
          checks before closing, and the page keeps every word of it one click away. */}
      <MoreAbout label="What happens to your data">
        <p className="settings-note">
          Closing is not deletion. Assessment data already stored stays in our database until we
          delete it on request; to ask, email{' '}
          <SupportLink subject={`Delete the data of workspace ${w.name}`} />. To revoke
          Defenssive’s access from your side as well, remove the Defenssive application from Microsoft
          Entra (Enterprise applications).
        </p>
        <p className="settings-note">
          Copies also stay in the backups our service makes, still readable by us: we delete each
          backup automatically about 90 days after it is made, and Azure keeps a deleted backup about
          30 days more, so a copy remains for about four months at most after a deletion. The{' '}
          <a href="/privacy" target="_blank" rel="noopener">
            privacy notice
          </a>{' '}
          has the detail, including the backups of our earlier pilot environment, which are not on that
          schedule.
        </p>
      </MoreAbout>

      {token === null ? (
        <button
          ref={startRef}
          type="button"
          className="btn btn-secondary btn-destructive-outline"
          disabled={busy}
          onClick={() => void start()}
          style={{ marginTop: 'var(--space-3)' }}
        >
          {working ? 'Starting…' : 'Close this workspace…'}
        </button>
      ) : (
        <div
          className="close-confirm"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !working) cancel()
          }}
        >
          <label htmlFor={`${promptId}-input`} id={promptId} className="settings-note">
            Type <strong style={{ fontWeight: 600 }}>{phrase}</strong> to confirm. This confirmation
            is valid for 5 minutes.
          </label>
          <input
            id={`${promptId}-input`}
            ref={inputRef}
            className="findings-search-input close-confirm-input"
            value={typed}
            autoComplete="off"
            spellCheck={false}
            disabled={working}
            onChange={(e) => setTyped(e.currentTarget.value)}
          />
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary btn-destructive"
              aria-describedby={promptId}
              disabled={!matches || busy}
              onClick={() => void close()}
            >
              {working ? 'Closing…' : 'Permanently close workspace'}
            </button>
            <button type="button" className="btn btn-ghost" disabled={working} onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div ref={statusRef} tabIndex={-1} aria-live="polite" style={{ outline: 'none' }}>
        {error ? (
          <p className="live-error" role="alert" style={{ marginTop: 'var(--space-2)' }}>
            {error}
          </p>
        ) : null}
      </div>
    </SettingsSection>
  )
}

/**
 * A destructive action behind a two-step inline confirm.
 *
 * Ending someone's access should take a deliberate second click, and a keyboard user should not lose
 * their place doing it: opening the confirm moves focus to Confirm, Cancel returns it to the trigger,
 * and Escape cancels. Confirm and Cancel are described by the prompt (aria-describedby), so a screen
 * reader reading either button also reads WHO it is about and WHAT it does — a bare "Confirm" out of
 * context is exactly the wrong thing to be certain about. The caller supplies the whole prompt because
 * "ends their access" is true of removing a member and false of withdrawing an unaccepted invitation.
 */
function ConfirmAction({
  verb,
  subject,
  prompt,
  onConfirm,
  disabled,
}: {
  verb: string
  subject: string
  prompt: string
  onConfirm: () => void
  disabled: boolean
}) {
  const [confirming, setConfirming] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  const returnFocus = useRef(false)
  const promptId = useId()

  useEffect(() => {
    if (confirming) {
      confirmRef.current?.focus()
    } else if (returnFocus.current) {
      returnFocus.current = false
      triggerRef.current?.focus()
    }
  }, [confirming])

  // Plain <button> (with the same `btn` classes the Button component applies) because these need refs
  // for focus management, and the shared Button does not forward one. Per-person accessible name so a
  // table of identical "Remove"/"Withdraw" buttons is navigable by screen reader; the visible verb is
  // contained in it (WCAG 2.5.3).
  if (!confirming) {
    return (
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-secondary"
        aria-label={`${verb} ${subject}`}
        disabled={disabled}
        onClick={() => setConfirming(true)}
      >
        {verb}
      </button>
    )
  }

  const cancel = () => {
    returnFocus.current = true
    setConfirming(false)
  }

  return (
    <span
      style={{ display: 'inline-flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') cancel()
      }}
    >
      <span id={promptId} className="settings-note">{prompt}</span>
      <button
        ref={confirmRef}
        type="button"
        className="btn btn-primary"
        aria-describedby={promptId}
        disabled={disabled}
        onClick={() => {
          onConfirm()
          setConfirming(false)
        }}
      >
        Confirm
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        aria-describedby={promptId}
        disabled={disabled}
        onClick={cancel}
      >
        Cancel
      </button>
    </span>
  )
}

/**
 * Sending an administrator back to the Microsoft consent screen.
 *
 * THE GAP THIS FILLS was found the hard way. Microsoft grants a tenant what the app registration
 * asked for AT THE MOMENT IT WAS APPROVED, so adding a capability leaves every existing customer
 * on the old set -- and this product had nowhere to send them. Once a connection is verified the
 * connect screen is never reached again: an administrator signs in, is taken straight to the
 * dashboard, and there is no way to approve anything. Adding Defender for Endpoint hit exactly
 * that, and was worked around with a URL pasted together by hand.
 *
 * NO CLAIM ABOUT WHAT IS GRANTED. It would be nicer to say "you are missing one permission", and
 * doing that means reading our own service principal's role assignments in the customer's
 * directory and diffing them against a list we keep here -- a list that drifts, in a place that
 * cannot see the registration. A confident "everything is granted" from a stale list is worse
 * than no claim, so this offers the action and explains when it is needed rather than deciding
 * for the reader.
 *
 * Owner or admin only, and the endpoint enforces it: consent decides which directory is assessed.
 */
/**
 * Attaches a Microsoft identity to the account you are already signed in to.
 *
 * THIS IS NOT A SIGN-IN BUTTON, and the distinction is the whole reason it exists. Signing in asks
 * Microsoft who you are. This tells Microsoft's answer where to attach -- to the account whose
 * session you are already holding.
 *
 * It replaces something that used to happen silently. An Entra sign-in carrying an address that
 * already had an account here used to be linked to it automatically, on the strength of the
 * asserting directory being connected to a workspace you belonged to. Anyone can create a
 * directory and invite you to a workspace, so that was a way into somebody else's account. Now the
 * only thing that attaches an identity is a session, and a session is something only you can hold.
 */
/**
 * The account-level sign-in card, which exists outside any workspace.
 *
 * Separate from the tenant card deliberately: a Microsoft identity belongs to a PERSON, and the
 * person who most needs to connect one may have no workspace at all.
 */
/**
 * Copies an identifier a person is asked to quote (the workspace ID, to support). The value stays
 * selectable text beside it, so where the browser refuses the clipboard nothing is lost.
 */
function CopyValue({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(timer)
  }, [copied])

  return (
    <Button
      variant="ghost"
      className="settings-copy"
      aria-label={copied ? `Copied the ${label}` : `Copy the ${label}`}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true), () => setCopied(false))
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  )
}

function AccountSignIn() {
  return (
    <SettingsSection
      title="Your sign-in"
      description={
        <p>
          Lets you sign in with Microsoft instead of a link. You will be asked to sign in to
          Microsoft even if you already are — connecting an account is deliberate, not a click.
        </p>
      }
    >
      <ConnectMicrosoft />
    </SettingsSection>
  )
}

function ConnectMicrosoft() {
  const [starting, setStarting] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  // The callback comes back here with a result in the query string, because a redirect cannot
  // return anything else.
  useEffect(() => {
    const outcome = new URLSearchParams(window.location.search).get('linked')
    if (!outcome) return

    setNote(linkedOutcomeMessage(outcome))
  }, [])

  async function go() {
    if (starting) return

    setStarting(true)
    setNote(null)

    const result = await startMicrosoftLink()

    if (result.ok && result.data.authorizeUrl) {
      window.location.assign(result.data.authorizeUrl)
      return
    }

    setStarting(false)
    setNote('That could not be started. Your account is unchanged.')
  }

  return (
    <>
      <Button variant="primary" className="settings-action" onClick={() => void go()} disabled={starting}>
        {starting ? 'Taking you to Microsoft…' : 'Connect Microsoft sign-in'}
      </Button>
      {note ? <p className="settings-hint is-note" role="status">{note}</p> : null}
    </>
  )
}

/**
 * Invitations you have been sent, and have not yet accepted.
 *
 * Nothing in this product could show these before. Every sign-in silently activated every pending
 * invitation anywhere, so being invited to a workspace and signing in were one act -- which meant
 * a stranger could put you in their workspace without you ever agreeing, and did not even need you
 * to read the email. Accepting is now something you do, to a workspace you can see.
 *
 * Renders nothing at all when there are none AND there is nothing to say, which is the ordinary
 * case. It stays on screen for a message about an invitation just accepted, because that
 * acceptance is what emptied the list.
 */
function Invitations() {
  const session = useSession()
  const [pending, setPending] = useState<PendingInvitation[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  /*
   * WHETHER ACCEPTING WOULD PRODUCE SOMETHING YOU CAN OPEN, asked BEFORE the button rather than
   * explained after it. Found by codex.
   *
   * A session carries one workspace and auth.attach_session_to_workspace only points one that
   * carries none (migration 0064). So somebody already in a workspace who accepts a second one
   * gets a real membership they cannot reach, the invitation is consumed on the way, and a note
   * appears saying so. Choosing a workspace at sign-in is a feature and it is not built; until it
   * is, the honest thing is not to spend somebody's invitation on a membership that does nothing.
   *
   * The invitation stays PENDING, so it is still there when the feature is.
   *
   * THREE STATES, NOT TWO. This was a boolean -- "known AND has a workspace" -- which put both
   * 'loading' and a FAILED session lookup on the eligible side and left the button live. Neither
   * is evidence that accepting would work; they are evidence that we do not know yet. The button
   * waits, and says what it is waiting for, rather than letting somebody spend an invitation to
   * find out. The server refuses the same case regardless, which is where the gate actually is.
   */
  const eligible: 'yes' | 'placed' | 'unknown' =
    session.state !== 'known'
      ? 'unknown'
      : session.me?.workspaceId
        ? 'placed'
        : 'yes'

  const placedIn = session.state === 'known' ? session.me?.workspaceName ?? null : null

  useEffect(() => {
    void fetchInvitations().then(result => {
      if (result.ok) {
        setPending(result.data.invitations)
        return
      }

      // NOT "no invitations". A failed fetch used to come back as an empty list and the card
      // disappeared, which reads as "nobody invited you". Say what actually happened.
      setNote(
        result.status === 401
          ? 'Your sign-in has ended, so we could not check for invitations. Sign in again.'
          : 'We could not check for invitations just now. Reload to try again.',
      )
    })
  }, [])

  async function decline(invitation: PendingInvitation) {
    if (busy) return
    setBusy(invitation.workspaceId)
    setNote(null)

    const result = await declineInvitation(invitation.workspaceId)
    setBusy(null)

    if (result.ok && result.data.declined) {
      // Gone, and nothing about this account changed. If it was blocking workspace creation, it
      // no longer is.
      setPending(current => current.filter(i => i.workspaceId !== invitation.workspaceId))
      setNote(`You declined the invitation to ${invitation.workspaceName}.`)
      return
    }

    if (result.ok === false && result.status === 401) {
      setNote('Your sign-in has ended. Sign in again to manage invitations.')
      return
    }

    setNote('That did not go through. The invitation is unchanged; try again in a moment.')
  }

  async function accept(invitation: PendingInvitation) {
    if (busy) return

    setBusy(invitation.workspaceId)

    const result = await acceptInvitation(invitation.workspaceId)

    if (result.ok && result.data.accepted) {
      /*
       * ACCEPTING CREATES A MEMBERSHIP. IT DOES NOT MOVE YOU.
       *
       * The session carries one workspace, and the accept endpoint only attaches when it carries
       * none -- so somebody who already had one joined and stayed exactly where they were, with no
       * route to what they had just accepted. Retrying could not help either: the membership is
       * active by then, so accepting again returns false and never reaches the attach.
       *
       * Selecting is a separate, deliberate act for that reason, and it re-checks membership in the
       * database rather than trusting this call. It also recovers the case where the attach inside
       * acceptance failed -- a rotation between the two writes -- which previously left somebody a
       * member of a workspace they could not open.
       */
      if (!result.data.attached) {
        const entered = await selectWorkspace(invitation.workspaceId)

        if (!entered.ok || !entered.data.attached) {
          /*
           * TWO DIFFERENT ANSWERS, AND THE FIRST VERSION GAVE BOTH THE SAME SENTENCE.
           *
           * `attached: false` with reason `session_already_placed` is the product's design:
           * auth.attach_session_to_workspace points a session forwards and only from nothing,
           * because re-pointing a LIVE session between tenants changes the meaning of every
           * capability already minted from it (migration 0064).
           *
           * Anything else -- a 404, a network failure, a reason we do not recognise -- establishes
           * nothing about where this session is. Saying "already open in another workspace" there
           * asserts a fact the call did not return, and sends somebody looking for a cause that
           * may not exist. Found by codex.
           */
          const placed = entered.ok && entered.data.reason === 'session_already_placed'

          setBusy(null)
          setNote(
            placed
              /*
               * NO LONGER "sign out and back in to work in it", which was advice that does not
               * work. A fresh sign-in does not ask which workspace: auth.workspace_for_user
               * returns the OLDEST active membership, so an ordinary sign-in reopens the one this
               * person already had and never the one they just accepted. Naming a workspace at
               * sign-in is a real feature and it is not built, so this says what is true today
               * rather than pointing at a route that leads back here. Found by codex.
               */
              ? `You have joined ${invitation.workspaceName}, and everything about that membership `
                + 'is saved. One sign-in opens one workspace, so to open it use Switch workspace '
                + 'in the account menu (top right): you sign in again and choose it.'
              : `You have joined ${invitation.workspaceName}, and that membership is saved. We `
                + 'could not confirm whether this session can open it. Reload; if it is still not '
                + `there, email ${SUPPORT_EMAIL}.`,
          )
          setPending(current => current.filter(i => i.workspaceId !== invitation.workspaceId))
          return
        }
      }

      window.location.reload()
      return
    }

    setBusy(null)

    /*
     * THE SERVER'S OWN REFUSAL, kept distinct from "this invitation is gone".
     *
     * /invitations/accept answers 409 with session_already_placed for a session that already names
     * a workspace, and it refuses BEFORE spending the invitation. So this one is still pending and
     * must stay in the list -- dropping it here would hide an invitation that is still live, which
     * is the opposite of what the refusal is for.
     */
    if (result.ok === false && result.status === 409) {
      /*
       * THE SERVER KNOWS SOMETHING THIS TAB DOES NOT: the session names a workspace. Eligibility
       * here came from the shell's single /auth/me fetch at mount and is stale -- another tab
       * accepted, or signed in. Repeating the note on every click, with the button still live,
       * told the person their invitation was waiting when the server had just refused it for a
       * reason this page could not see. Reloading re-reads the session and renders the truth --
       * usually the workspace they were just placed in. Found by the independent review.
       */
      window.location.reload()
      return
    }

    /*
     * ONLY AN ANSWER FROM THE SERVER CAN SAY THE INVITATION IS GONE.
     *
     * Every failure used to fall through to the filter below under the comment "either way it is
     * not pending any more" -- and for most of them that was simply asserted. A dropped connection
     * (status 0) never reached the server; a 401 was refused before acceptance ran; a 5xx says
     * nothing about the row. In each the invitation is still pending in the database and still
     * returned by /auth/invitations, and the page dropped it without a word. When it was the only
     * one, the whole card unmounted -- visually the same as having accepted it. Found by the
     * independent review.
     *
     * So the row leaves the list only when the server ANSWERED and said it was not accepted (a
     * 200 with accepted:false, or a 404): those establish the row is spent or not theirs. Anything
     * else keeps it, and says why.
     */
    if (result.ok === false && result.status !== 404) {
      setNote(
        result.status === 0
          ? 'We could not reach the server. That invitation is still waiting for you — try again.'
          : result.status === 401
            /*
             * NOT "sign in again" on its own. A fresh sign-in opens the oldest active membership
             * -- or, for somebody with none, the connect screen, which now shows pending
             * invitations before it lets anybody create a workspace. So the true instruction is
             * where to look after signing in, not merely to do it. Found by the independent review.
             */
            ? 'Your sign-in has ended, so nothing was accepted. Sign in again and you will be ' +
              'shown this invitation before anything else — it is still waiting for you.'
            : 'That did not go through, and nothing was accepted. The invitation is still ' +
              'waiting for you — try again in a moment.',
      )
      return
    }

    // Refused by the server, or already gone. Either way it is not pending any more.
    setPending(current => current.filter(i => i.workspaceId !== invitation.workspaceId))
  }

  /*
   * THE NOTE OUTLIVES THE LIST, WHICH IS THE ONLY REASON IT IS WORTH SETTING.
   *
   * This read `if (pending.length === 0) return null`, and accepting removes the invitation from
   * pending -- so accepting the LAST one unmounted the card in the same render that set the
   * message explaining what had just happened. The message existed only while another invitation
   * was still waiting, which is exactly when it was least needed. Found by codex.
   */
  if (pending.length === 0 && !note) return null

  return (
    <Card title="Invitations" style={{ marginTop: 'var(--space-4)' }}>
      {pending.length === 0 ? null : eligible === 'placed' ? (
        /*
         * SAID BEFORE THE BUTTON, not after it, and this is the second attempt at it: the first
         * edit aborted half-applied, so the old lede below stayed and the restriction was never
         * rendered at all. The page promised "accepting lets you read every finding in it" and
         * then, once the membership existed and the invitation was spent, explained that this
         * session could not open it. Found by codex, twice.
         */
        <p className="settings-lede">
          You are signed in to <strong>{placedIn ?? 'a workspace'}</strong>, and one sign-in opens
          one workspace, so these are held here rather than accepted. To accept one, sign out (account
          menu, top right) and sign in again: you are offered it before any workspace opens.
        </p>
      ) : eligible === 'unknown' ? (
        /*
         * NOT KNOWN YET, AND NOT ASSUMED EITHER WAY. `placed` was computed as "known AND has a
         * workspace", so both 'loading' and a FAILED session lookup read as eligible and left the
         * button live -- the accept then reached the server, which is where the real gate is, but
         * the customer learned the restriction by hitting it. Found by codex.
         */
        <p className="settings-lede">
          Checking which workspace this sign-in is for. Accepting an invitation is available once
          that is known.
        </p>
      ) : (
        <p className="settings-lede">
          Someone has invited you to a workspace. Accepting lets you read every finding in it, and
          lets its owner see that you are a member.
        </p>
      )}

      {note ? <p className="settings-hint is-note" role="status">{note}</p> : null}

      {pending.length === 0 ? null : (
        <TableScroll label="Invitations waiting for you">
        <table className="settings-table">
          <tbody>
            {pending.map(invitation => (
              <tr key={invitation.workspaceId}>
                <td>{invitation.workspaceName}</td>
                <td><Tag>{memberRoleLabel(invitation.role)}</Tag></td>
                <td style={{ textAlign: 'right' }}>
                  <Button
                    variant="ghost"
                    onClick={() => void accept(invitation)}
                    disabled={eligible !== 'yes' || busy === invitation.workspaceId}
                  >
                    {busy === invitation.workspaceId ? 'Accepting…' : 'Accept'}
                  </Button>
                  {/* Always available, even when Accept is not: declining is how somebody already
                      in a workspace clears an invitation that would otherwise block nothing but
                      still sits there. */}
                  <Button
                    variant="ghost"
                    onClick={() => void decline(invitation)}
                    disabled={busy === invitation.workspaceId}
                  >
                    Decline
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      )}
    </Card>
  )
}

function Reapprove() {
  const [starting, setStarting] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  async function go() {
    if (starting) return

    setStarting(true)
    setFailed(null)

    const result = await startConsent()

    if (result.ok && result.data.consentUrl) {
      // Microsoft's own screen. It lists what is being requested, so the person approving reads
      // it from Microsoft rather than from us -- which is the only version of that list they
      // have any reason to trust.
      window.location.assign(result.data.consentUrl)
      return
    }

    setStarting(false)

    setFailed(
      result.ok
        ? 'Microsoft did not return a consent link.'
        : result.status === 403
          ? 'Only an owner or an admin can re-approve permissions.'
          : 'That could not be started. Nothing in your tenant has changed.',
    )
  }

  return (
    <>
      <Button variant="primary" onClick={() => void go()} disabled={starting}>
        {starting ? 'Taking you to Microsoft…' : 'Re-approve permissions'}
      </Button>

      <MoreAbout label="When to re-approve">
        <p className="settings-note">
          Microsoft grants us what we asked for when you first approved. If we have added a
          capability since, it stays ungranted until an administrator approves the updated list —
          the assessment will say so rather than failing quietly. Re-approving is safe at any time
          and never widens access beyond what Microsoft shows you on its own screen.
        </p>
      </MoreAbout>

      {failed ? (
        <p className="live-error" style={{ marginTop: 'var(--space-2)' }}>{failed}</p>
      ) : null}
    </>
  )
}

/**
 * Inviting the rest of the team.
 *
 * SHOWN TO EVERYONE, REFUSED BY THE DATABASE. Hiding the form from non-owners would be a nicer
 * screen and a worse guarantee: the page would then be the thing deciding who may invite, and
 * anybody can call the endpoint directly. auth.invite_member refuses a non-owner whatever the
 * browser renders, so the form's job is to carry the refusal back in words rather than to
 * pre-empt it. What it does do is say up front that only owners can, so nobody types an address
 * to find that out.
 *
 * OWNER IS ABSENT FROM THE LIST, and not because the list is short. Ownership is the role that
 * can invite, so granting it grants the power to grant; the database refuses it by name and this
 * simply does not offer it.
 */
function InvitePanel({
  workspace,
  mutations,
}: {
  workspace: WorkspaceSettings
  mutations: SettingsMutations
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('read_only')
  // "This card is the active write", distinct from the page-wide `busy`: the shared lock disables the
  // button whenever ANY member write runs, but only an invite this form started should say "Inviting…".
  const [mine, setMine] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)

  const busy = mutations.busy
  const connected = workspace.verificationState === 'tenant_verified'

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (busy) return

    setFailed(null)
    setSent(null)
    const target = email.trim()
    setMine(true)

    // Through the SHARED controller, so an invite holds the same lock as a table write and — like a
    // table write — a 401/403 refusal refreshes the page, recomputing who may manage members. Without
    // that, a demoted owner who happens to try Invite first would keep stale Remove/role controls.
    await mutations.run(() => inviteMember(target, role), {
      onResult: (result) => {
        if (result.ok) {
          // The refresh (driven by the controller) brings the new pending row into the table above,
          // rather than naming the invitee only in a note.
          setSent(target)
          setEmail('')
          return
        }

        // The server's own sentence. Every refusal here is specific -- not an owner, already a
        // member, no verified tenant -- and a generic "could not invite" would make the reader
        // guess which.
        setFailed(result.message)
      },
    })

    setMine(false)
  }

  return (
    <SettingsSection
      title="Invite somebody"
      description={
        <p>
          Only an owner can invite. Whoever you invite will be able to read every finding in this
          workspace and the evidence behind it, which carries your users' names and your policy
          contents.
        </p>
      }
    >

      {!connected ? (
        <p className="report-warning">
          This workspace has no verified Microsoft tenant yet, so it cannot admit anybody.{' '}
          <a href="/connect">Connect Microsoft 365</a> first.
        </p>
      ) : null}

      <form className="invite-form invite-form-wide" onSubmit={submit}>
        <label className="invite-field">
          <span className="invite-label">Email address</span>
          <input
            className="findings-search-input"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setFailed(null) }}
            placeholder="name@company.com"
            autoComplete="off"
            required
          />
        </label>

        <fieldset className="invite-roles">
          <legend className="invite-label">What they can do</legend>

          {INVITABLE_ROLES.map((option) => (
            <label key={option.value} className="invite-role">
              <input
                type="radio"
                name="invite-role"
                value={option.value}
                checked={role === option.value}
                onChange={() => setRole(option.value)}
              />
              <span>
                <strong>{option.label}</strong>
                <span className="settings-note" style={{ display: 'block', marginTop: 0 }}>
                  {option.what}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        <Button type="submit" variant="primary" disabled={busy || email.trim().length === 0}>
          {mine ? 'Inviting…' : 'Send invitation'}
        </Button>
      </form>

      {/* Live-region semantics so a screen reader hears the outcome of a submit it did not see
          rendered: the refusal is assertive, the confirmation polite. */}
      {failed ? (
        <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>{failed}</p>
      ) : null}

      {sent ? (
        <p className="settings-note" role="status" style={{ marginTop: 'var(--space-3)' }}>
          Invited <span className="mono">{sent}</span>. They appear above as pending until they
          sign in with that address, and the invitation grants nothing until they do.
        </p>
      ) : null}
    </SettingsSection>
  )
}

/**
 * The connection's own word for its state, not a re-scoring of it.
 *
 * 'degraded' and 'reconnecting' are real states this product distinguishes, and flattening them
 * to "connected" would hide the case where a scan is running with a permission it no longer has.
 */
function ConnectionStatus({ status }: { status: string }) {
  if (status === 'verified') return <Tag tone="accent">Verified</Tag>
  if (status === 'disconnected') return <Tag tone="danger">Disconnected</Tag>

  return <Tag tone="warn">{status}</Tag>
}
